import {
  Box,
  Button,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import SelectOptionsApi from "../../../components/SelectApi";
import CSVDownloadButton from "../../../components/downloadCSV";
import { api } from "../../../config/apis";
import AdminApi from "../../../config/apis/admin";
import { DataLoader, toTitleCase } from "../../../helper/helperFunction";
import { GENDER_OPTIONS } from "./filter-options";
import { buildSponsorParams } from "./filter-params";
import styles from "./style.module.css";

const EMPTY = "-";

const showText = (value) => {
  if (value === null || value === undefined || value === "") {
    return EMPTY;
  }
  return value;
};

const showAmount = (value) => {
  if (value === null || value === undefined || value === "") {
    return EMPTY;
  }
  const amount = Number(value);
  return Number.isNaN(amount) ? String(value) : `$${amount.toFixed(2)}`;
};

/**
 * The endpoint sends the stored value ("male"), so it is matched back to the
 * filter's own option to keep the column and the Gender dropdown reading the
 * same; anything unrecognised falls back to title case.
 */
const showGender = (value) => {
  if (value === null || value === undefined || value === "") {
    return EMPTY;
  }
  const option = GENDER_OPTIONS.find(
    (item) => item.id === String(value).toLowerCase()
  );
  return option ? option.name : toTitleCase(String(value));
};

const showPercentage = (value) => {
  if (value === null || value === undefined || value === "") {
    return EMPTY;
  }
  const number = Number(value);
  return Number.isNaN(number) ? String(value) : `${number}%`;
};

const showList = (value) => {
  if (!Array.isArray(value)) {
    return showText(value);
  }
  const items = value.filter(
    (item) => item !== null && item !== undefined && item !== ""
  );
  return items.length > 0 ? items.join(", ") : EMPTY;
};

const showYesNo = (value) => {
  if (value === null || value === undefined || value === "") {
    return EMPTY;
  }
  return value ? "Yes" : "No";
};

/**
 * A student can hold more than one placement, so each job column keeps one
 * entry per placement rather than showing only the first one.
 */
const placementValues = (row, format) => {
  const placements = Array.isArray(row.job_placements) ? row.job_placements : [];
  return placements.map(format);
};

const JOB_COLUMNS = [
  { name: "Job Type", width: "140px", format: (job) => showText(job.job_type) },
  {
    name: "Company Name",
    width: "180px",
    format: (job) => showText(job.company_name),
  },
  {
    name: "Job Title",
    width: "160px",
    format: (job) => showText(job.job_title),
  },
  {
    name: "Joining Date",
    width: "145px",
    format: (job) => showText(job.joining_date),
  },
  { name: "Left Job", width: "110px", format: (job) => showYesNo(job.left_job) },
  {
    name: "End Date",
    width: "130px",
    format: (job) => showText(job.end_date),
  },
  {
    name: "Leaving Reason",
    width: "180px",
    format: (job) => showText(job.leaving_reason),
  },
  { name: "Salary", width: "130px", format: (job) => showAmount(job.salary) },
  {
    name: "Working Hours",
    width: "150px",
    format: (job) => showText(job.working_hours),
  },
  {
    name: "Work Experience",
    width: "165px",
    format: (job) => showText(job.work_experience),
  },
];

export const SponsoredStudents = ({ sponsorFilters }) => {
  // "Search By" group - typed/picked in the bar, only applied when Go is hit.
  const [searchData, setSearchData] = useState({});
  const [search, setSearch] = useState({});
  // Location/gender and the sort order apply the moment they change, the way
  // the rest of the admin grids behave - they are not part of the Go group.
  const [dropdownFilters, setDropdownFilters] = useState({});
  const [sortOrder, setSortOrder] = useState("asc");
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageState, setPageState] = useState({ key: "", page: 1 });
  const [loading, setLoading] = useState(true);

  // Any filter change invalidates the current page number, so it is derived
  // rather than reset in an effect - that way a filtered request never asks the
  // server for a page the new result set doesn't have.
  const filterKey = JSON.stringify({
    ...sponsorFilters,
    ...search,
    ...dropdownFilters,
    sortOrder,
  });
  const page = pageState.key === filterKey ? pageState.page : 1;

  const handleSearchUpdate = (name, value) => {
    setSearchData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDropdownUpdate = (name, value) => {
    setDropdownFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // A fresh object every time, so hitting Go/Enter always refetches - even when
  // the search values themselves haven't changed since the last run.
  const handleApplyFilter = () => {
    setSearch({ ...searchData });
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleApplyFilter();
    }
  };

  const handlePageChange = (nextPage) => {
    setPageState({ key: filterKey, page: nextPage });
  };

  const handleRowsPerPageChange = (nextRowsPerPage) => {
    setRowsPerPage(nextRowsPerPage);
    setPageState({ key: filterKey, page: 1 });
  };

  const params = useMemo(() => {
    const query = {
      ...buildSponsorParams(sponsorFilters),
      page,
      limit: rowsPerPage,
      sort_order: sortOrder,
    };

    // `id` and `place` are the only two the endpoint reads as filters[...].
    const nested = {};
    if (search.id) {
      nested.id = String(search.id).trim();
    }
    if (search.place) {
      nested.place = search.place;
    }
    if (Object.keys(nested).length > 0) {
      query.filters = nested;
    }

    if (search.keyword) {
      query.keyword = String(search.keyword).trim();
    }
    if (dropdownFilters.location) {
      query.location = dropdownFilters.location;
    }
    if (dropdownFilters.gender) {
      query.gender = dropdownFilters.gender;
    }

    return query;
  }, [sponsorFilters, search, dropdownFilters, sortOrder, page, rowsPerPage]);

  useEffect(() => {
    let active = true;

    const fetchStudents = async () => {
      try {
        setLoading(true);
        const res = await api.get(AdminApi.DASH_SPONSOR_DATA_STUDENTS, {
          params,
        });
        if (!active) return;
        setRows(res.data.data ?? []);
        setTotalRows(res.data.meta?.totalItems ?? 0);
      } catch (error) {
        console.error(error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchStudents();

    return () => {
      active = false;
    };
  }, [params]);

  const tableColumns = [
    {
      selector: (row) => showText(row.id),
      name: "ID",
      width: "80px",
    },
    {
      cell: (row) => (
        <span className={styles.student_name}>
          {row.student_name ? toTitleCase(row.student_name) : EMPTY}
        </span>
      ),
      name: "Student's Name",
      minWidth: "180px",
    },
    {
      selector: (row) => showText(row.email),
      name: "Email",
      minWidth: "200px",
    },
    {
      selector: (row) => showText(row.address),
      name: "Address",
      minWidth: "180px",
      wrap: true,
    },
    {
      selector: (row) => showText(row.age),
      name: "Age",
      width: "90px",
    },
    {
      selector: (row) => showGender(row.gender),
      name: "Gender",
      width: "110px",
    },
    {
      selector: (row) => showText(row.education),
      name: "Education",
      minWidth: "150px",
      wrap: true,
    },
    {
      selector: (row) => showText(row.family_income),
      name: "Family Income",
      minWidth: "170px",
    },
    {
      selector: (row) => showList(row.digital_skills?.household_items),
      name: "Household Items",
      minWidth: "190px",
      wrap: true,
    },
    {
      selector: (row) => showText(row.digital_skills?.smartphone),
      name: "Smartphone",
      minWidth: "140px",
    },
    {
      selector: (row) => showText(row.digital_skills?.laptop_desktop),
      name: "Laptop / Desktop",
      minWidth: "165px",
    },
    {
      selector: (row) => showText(row.course_name),
      name: "Course Name",
      minWidth: "185px",
      wrap: true,
    },
    {
      selector: (row) => showText(row.cohort_name),
      name: "Cohort Name",
      minWidth: "185px",
    },
    {
      selector: (row) => showText(row.section_name),
      name: "Section Name",
      minWidth: "150px",
    },
    {
      selector: (row) => showText(row.classes_start_date),
      name: "Classes Start Date",
      minWidth: "180px",
    },
    {
      selector: (row) => showText(row.classes_end_date),
      name: "Classes End Date",
      minWidth: "175px",
    },
    {
      selector: (row) => showPercentage(row.attendance_percentage),
      name: "Attendance",
      minWidth: "140px",
    },
    {
      selector: (row) => showText(row.final_grade),
      name: "Final Grade",
      minWidth: "140px",
    },
    {
      selector: (row) => showText(row.flag_status),
      name: "Flag Student",
      minWidth: "150px",
    },
    {
      selector: (row) => showText(row.organization),
      name: "Organization",
      minWidth: "180px",
    },
    {
      selector: (row) => showAmount(row.amount_sponsored),
      name: "Amount Sponsored",
      minWidth: "180px",
    },
    ...JOB_COLUMNS.map((column) => ({
      name: column.name,
      minWidth: column.width,
      wrap: true,
      cell: (row) => {
        const values = placementValues(row, column.format);
        if (values.length === 0) {
          return EMPTY;
        }
        return (
          <Box className={styles.stacked_cell}>
            {values.map((value, index) => (
              <span key={index}>{value}</span>
            ))}
          </Box>
        );
      },
    })),
  ];

  // The rows carry nested objects, so they are flattened before handing them to
  // the CSV link - otherwise every nested field lands as [object Object].
  const csvRows = useMemo(
    () =>
      rows.map((row) => ({
        ID: showText(row.id),
        "Student's Name": showText(row.student_name),
        Email: showText(row.email),
        Address: showText(row.address),
        Age: showText(row.age),
        Gender: showGender(row.gender),
        Education: showText(row.education),
        "Family Income": showText(row.family_income),
        "Household Items": showList(row.digital_skills?.household_items),
        Smartphone: showText(row.digital_skills?.smartphone),
        "Laptop / Desktop": showText(row.digital_skills?.laptop_desktop),
        "Course Name": showText(row.course_name),
        "Cohort Name": showText(row.cohort_name),
        "Section Name": showText(row.section_name),
        "Classes Start Date": showText(row.classes_start_date),
        "Classes End Date": showText(row.classes_end_date),
        Attendance: showPercentage(row.attendance_percentage),
        "Final Grade": showText(row.final_grade),
        "Flag Student": showText(row.flag_status),
        Organization: showText(row.organization),
        "Amount Sponsored": showAmount(row.amount_sponsored),
        ...JOB_COLUMNS.reduce((columns, column) => {
          const values = placementValues(row, column.format);
          columns[column.name] = values.length > 0 ? values.join(" | ") : EMPTY;
          return columns;
        }, {}),
      })),
    [rows]
  );

  return (
    <Box className={styles.students_section}>
      {/* noValidate: the shared select renders a `required` native input, which
          would otherwise block submitting until every dropdown is picked. Each
          filter here is optional and independent of the others. */}
      <form
        noValidate
        className={styles.filter_bar}
        onSubmit={(event) => {
          event.preventDefault();
          handleApplyFilter();
        }}
      >
        <Box className={styles.filter_group}>
          <Typography variant="body1" className={styles.filter_label}>
            Search By
          </Typography>
          <Box className={styles.input_id}>
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              type="number"
              placeholder="ID"
              value={searchData.id ?? ""}
              onChange={(event) => handleSearchUpdate("id", event.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </Box>
          <Box className={styles.select_place}>
            <SelectOptionsApi
              apiEndpoint={AdminApi.CITIES}
              formData={searchData}
              onUpdate={handleSearchUpdate}
              labelKey="title"
              valueKey="id"
              name="place"
              errorText=""
              paramId={""}
              defaultText="Place"
            />
          </Box>
          <span className={styles.span_or}>Or</span>
          <Box className={styles.input_keyword}>
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              placeholder="Keyword"
              value={searchData.keyword ?? ""}
              onChange={(event) =>
                handleSearchUpdate("keyword", event.target.value)
              }
              onKeyDown={handleSearchKeyDown}
            />
          </Box>
          <Button type="submit" className={styles.go_btn}>
            Go
          </Button>
        </Box>

        <Box className={styles.filter_group}>
          <Typography variant="body1" className={styles.filter_label}>
            Location
          </Typography>
          <Box className={styles.select_location}>
            <SelectOptionsApi
              apiEndpoint={AdminApi.LOCATION_PAGINATE}
              formData={dropdownFilters}
              onUpdate={handleDropdownUpdate}
              labelKey="name"
              valueKey="id"
              name="location"
              errorText=""
              paramId={""}
              defaultText="Location"
            />
          </Box>
          <Typography variant="body1" className={styles.filter_label}>
            Gender
          </Typography>
          <Box className={styles.select_gender}>
            {/* Plain select so the blank option stays selectable - picking it
                again clears the filter. */}
            <Select
              fullWidth
              size="small"
              displayEmpty
              className="select"
              value={dropdownFilters.gender ?? ""}
              onChange={(event) =>
                handleDropdownUpdate("gender", event.target.value)
              }
            >
              <MenuItem value="">
                <em className="select">Gender</em>
              </MenuItem>
              {GENDER_OPTIONS.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box className={styles.select_order}>
            <Select
              fullWidth
              size="small"
              className="select"
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value)}
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
          </Box>
          <Box className={styles.download_btn}>
            <CSVDownloadButton
              fileName={"Sponsored-Students"}
              data={csvRows}
              downloadText="Download"
            />
          </Box>
        </Box>
      </form>

      <div className="page-container table-phases sponsor_data_sticky">
        <DataTable
          columns={tableColumns}
          data={rows}
          highlightOnHover
          fixedHeader
          fixedHeaderScrollHeight="calc(100vh - 260px)"
          progressPending={loading}
          progressComponent={<DataLoader />}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationDefaultPage={page}
          paginationPerPage={rowsPerPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
        />
      </div>
    </Box>
  );
};

export default SponsoredStudents;
